import { PASSWORD_MIN_LENGTH } from '@mcgames/contracts';
import { Button, TextInput } from '@mcgames/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../api/auth-store.ts';
import { useChangePassword, useErrorText } from '../api/hooks.ts';
import { HttpError } from '../api/http.ts';
import own from './AccountPage.module.css';
import styles from './Admin.module.css';

/** The signed-in user's own account. */
export function AccountPage() {
  const { t } = useTranslation('admin');
  const session = useSession();

  return (
    <div className={styles['page']}>
      <h1 className={styles['heading']}>{t('account.title')}</h1>
      {session.user !== null && (
        <p className={styles['muted']}>
          {t('header.signedInAs', { username: session.user.username })}
        </p>
      )}
      <ChangePasswordForm />
    </div>
  );
}

type FieldErrors = { current?: string; next?: string; confirm?: string };

function ChangePasswordForm() {
  const { t } = useTranslation('admin');
  const errorText = useErrorText();
  const change = useChangePassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  function validate(): FieldErrors {
    const found: FieldErrors = {};
    if (current === '') found.current = t('account.changePassword.validation.currentRequired');
    if (next.length < PASSWORD_MIN_LENGTH) {
      found.next = t('account.changePassword.validation.tooShort', { min: PASSWORD_MIN_LENGTH });
    } else if (next === current) {
      found.next = t('account.changePassword.validation.unchanged');
    }
    if (next !== confirm) found.confirm = t('account.changePassword.validation.mismatch');
    return found;
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    change.mutate(
      { currentPassword: current, newPassword: next },
      {
        onSuccess: () => {
          setCurrent('');
          setNext('');
          setConfirm('');
        },
      },
    );
  }

  // The shared INVALID_CREDENTIALS text talks about the username, which this form does not have.
  const failure =
    change.error instanceof HttpError && change.error.code === 'INVALID_CREDENTIALS'
      ? t('account.changePassword.currentWrong')
      : change.isError
        ? errorText(change.error)
        : null;

  return (
    <section className={styles['section']} aria-labelledby="change-password-title">
      <h2 id="change-password-title" className={styles['subheading']}>
        {t('account.changePassword.title')}
      </h2>
      <p className={styles['muted']}>{t('account.changePassword.hint')}</p>
      <form className={own['form']} onSubmit={submit} noValidate>
        <div>
          <TextInput
            label={t('account.changePassword.current')}
            type="password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            autoComplete="current-password"
            required
            {...(errors.current !== undefined && { error: errors.current })}
          />
        </div>
        <div>
          <TextInput
            label={t('account.changePassword.new')}
            type="password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            autoComplete="new-password"
            required
            {...(errors.next !== undefined && { error: errors.next })}
          />
          <span className={own['hint']}>
            {t('account.changePassword.newHint', { min: PASSWORD_MIN_LENGTH })}
          </span>
        </div>
        <div>
          <TextInput
            label={t('account.changePassword.confirm')}
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
            required
            {...(errors.confirm !== undefined && { error: errors.confirm })}
          />
        </div>
        {failure !== null && (
          <p className={styles['error']} role="alert">
            {failure}
          </p>
        )}
        {change.isSuccess && (
          <p className={styles['status']} role="status">
            {t('account.changePassword.success')}
          </p>
        )}
        <div className={styles['row']}>
          <Button type="submit" disabled={change.isPending}>
            {t('account.changePassword.submit')}
          </Button>
        </div>
      </form>
    </section>
  );
}
